import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { DRAWER_CLASS, SMALL_ICON_BUTTON_CLASS } from '@/lib/styles';
import { memberHeading } from '@/lib/utils';

interface SideDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Member shown under the title as "#id - name". */
  member?: { id: string; name: string; memberNo?: string } | null;
  /** Small gray tag after the member, e.g. the policy number. */
  tag?: string;
  /** Extra control next to the title, e.g. an Edit button. */
  titleAction?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

/** Right-side drawer with the app's standard header, scrolling body and optional footer. */
export function SideDrawer({
  open,
  onOpenChange,
  title,
  member,
  tag,
  titleAction,
  footer,
  children,
}: SideDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className={DRAWER_CLASS}>
        <DrawerHeader className="shrink-0 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <DrawerTitle className="text-base font-semibold text-gray-900">{title}</DrawerTitle>
                {titleAction}
              </div>
              {member && (
                <p className="mt-0.5 flex flex-wrap items-center gap-2 truncate text-sm font-medium tabular-nums text-[#4369a0]">
                  {memberHeading(member)}
                  {tag && (
                    <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[13px] font-semibold text-gray-800">
                      {tag}
                    </span>
                  )}
                </p>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" className={SMALL_ICON_BUTTON_CLASS}>
                    <X className="h-3.5 w-3.5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </DrawerClose>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          </div>
        </DrawerHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto scrollbar-hide bg-gray-50/80 px-6 py-5">
          {children}
        </div>

        {footer && (
          <DrawerFooter className="shrink-0 flex-row justify-end gap-2 border-t border-gray-200 bg-white px-6 py-4">
            {footer}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
